import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useApi } from '../../../shared/hooks/useApi'
import { BarChart3, Database, TrendingUp, Building2, PieChart, RefreshCw } from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Cell,
  Pie,
} from 'recharts'
import CPVAnalysis from './CPVAnalysis'

interface StatsData {
  total_notices?: number
  database_size_mb?: number
  notices_by_type?: Record<string, number>
  last_sync?: string
}

interface OverviewData {
  open_tenders?: number
  closed_tenders?: number
  total_notices?: number
  unique_organizations?: number
}

interface CPVData {
  divisions?: Array<{
    code: string
    total_count: number
    open_count: number
    closed_count: number
  }>
}

interface OrganizationData {
  organization_name: string
  total_count: number
  open_count: number
  closed_count: number
}

interface ContractorData {
  contractor_name: string
  contractor_uuid: string
  wins_count: number
}

interface RefreshStatus {
  status?: string
  current_phase?: string
  phase_progress?: number
  phase_description?: string
  last_refresh?: string
  records_total?: number
  records_processed?: number
}

interface DashboardData {
  overview: OverviewData | null
  cpvData: CPVData | null
  organizations: OrganizationData[]
  contractors: ContractorData[]
}

interface FilterUpdates {
  territory_id?: string
  date_from?: string
  date_to?: string
  cpv?: string
  year?: string | number
}

interface StatCardProps {
  title: string
  value: string
  icon: React.ComponentType<{ className?: string }>
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red'
}

function StatCard({ title, value, icon: Icon, color = 'blue' }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-brand-bg text-brand',
    green: 'bg-success/10 text-success',
    purple: 'bg-ai/10 text-ai',
    orange: 'bg-warning/10 text-warning',
    red: 'bg-danger/10 text-danger',
  }

  return (
    <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-ink-secondary">{title}</p>
          <p className="text-2xl font-semibold text-ink">{value}</p>
        </div>
      </div>
    </div>
  )
}

// Dashboard Tab - Overview with charts
function DashboardTab({
  overview,
  cpvData,
  organizations,
  contractors,
  onContractorClick,
}: {
  overview?: OverviewData | null
  cpvData?: CPVData | null
  organizations?: OrganizationData[]
  contractors?: ContractorData[]
  onContractorClick?: (contractorUuid: string) => void
}) {
  // Prepare chart data
  const statusData = overview
    ? [
        { name: 'Otwarte', value: overview.open_tenders, color: 'var(--color-success)' },
        { name: 'Zamknięte', value: overview.closed_tenders, color: 'var(--color-danger)' },
      ]
    : []

  const cpvChartData =
    cpvData?.divisions?.slice(0, 10).map((div) => ({
      name: div.code,
      ogłoszenia: div.total_count,
      otwarte: div.open_count,
      zamknięte: div.closed_count,
    })) || []

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Otwarte postępowania"
            value={overview.open_tenders?.toLocaleString('pl-PL') || '0'}
            icon={TrendingUp}
            color="green"
          />
          <StatCard
            title="Zamknięte postępowania"
            value={overview.closed_tenders?.toLocaleString('pl-PL') || '0'}
            icon={Database}
            color="red"
          />
          <StatCard
            title="Razem ogłoszeń"
            value={overview.total_notices?.toLocaleString('pl-PL') || '0'}
            icon={BarChart3}
            color="blue"
          />
          <StatCard
            title="Unikalnych zamawiających"
            value={overview.unique_organizations?.toLocaleString('pl-PL') || '0'}
            icon={Building2}
            color="purple"
          />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Pie Chart */}
        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <h3 className="text-lg font-medium text-ink mb-4">Status postępowań</h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-surface-raised)', border: '1px solid var(--color-border)', color: 'var(--color-ink)' }} />
              </RechartsPieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-ink-muted">Brak danych do wyświetlenia</p>
          )}
        </div>

        {/* CPV Bar Chart */}
        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <h3 className="text-lg font-medium text-ink mb-4">Top 10 kategorii CPV</h3>
          {cpvChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={cpvChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="name" tick={{ fill: 'var(--color-ink-muted)' }} />
                <YAxis tick={{ fill: 'var(--color-ink-muted)' }} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-surface-raised)', border: '1px solid var(--color-border)', color: 'var(--color-ink)' }} />
                <Bar dataKey="otwarte" stackId="a" fill="var(--color-success)" name="Otwarte" />
                <Bar dataKey="zamknięte" stackId="a" fill="var(--color-danger)" name="Zamknięte" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-ink-muted">Brak danych do wyświetlenia</p>
          )}
        </div>
      </div>

      {/* Top Organizations and Contractors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <h3 className="text-lg font-medium text-ink mb-4">Najaktywniejsi zamawiający</h3>
          {organizations && organizations.length > 0 ? (
            <div className="space-y-3">
              {organizations.slice(0, 5).map((org, index: number) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-3 bg-surface rounded"
                >
                  <div>
                    <p className="font-medium text-ink">{org.organization_name}</p>
                    <p className="text-sm text-ink-secondary">{org.total_count} ogłoszeń</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-success">{org.open_count} otwarte</p>
                    <p className="text-sm text-danger">{org.closed_count} zamknięte</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-ink-muted">Brak danych do wyświetlenia</p>
          )}
        </div>

        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <h3 className="text-lg font-medium text-ink mb-4">Liderzy wykonawców</h3>
          {contractors && contractors.length > 0 ? (
            <div className="space-y-3">
              {contractors.slice(0, 5).map((contractor: ContractorData, index: number) => (
                <button
                  key={index}
                  type="button"
                  className="flex justify-between items-center p-3 bg-surface rounded cursor-pointer hover:bg-surface-hover w-full text-left"
                  onClick={() => onContractorClick && onContractorClick(contractor.contractor_uuid)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onContractorClick && onContractorClick(contractor.contractor_uuid)
                    }
                  }}
                >
                  <div>
                    <p className="font-medium text-ink">{contractor.contractor_name}</p>
                    <p className="text-sm text-ink-secondary">{contractor.wins_count} zwycięstw</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-ink-muted">Brak danych do wyświetlenia</p>
          )}
        </div>
      </div>
    </div>
  )
}

// Tab Navigation Component
function TabNavigation({
  activeTab,
  onTabChange,
  tabs,
}: {
  activeTab: string
  onTabChange: (tab: string) => void
  tabs: { id: string; label: string; icon: React.ComponentType<{ className?: string }> }[]
}) {
  return (
    <div className="border-b border-border">
      <nav className="-mb-px flex space-x-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === tab.id
                ? 'border-brand text-brand'
                : 'border-transparent text-ink-muted hover:text-ink-secondary hover:border-border'
            }`}
          >
            <tab.icon className="h-5 w-5 inline mr-2" />
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  )
}

const StatisticsPanel = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    getStatisticsOverview,
    getCPVBreakdown,
    getOrganizations,
    getContractors,
    refreshStatistics,
    getRefreshStatus,
    apiCall,
  } = useApi()

  // State for refresh status
  const [refreshStatus, setRefreshStatus] = useState<RefreshStatus | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [currentPhase, setCurrentPhase] = useState<string>('')
  const [phaseProgress, setPhaseProgress] = useState<number>(0)
  const [phaseDescription, setPhaseDescription] = useState<string>('')

  // Poll refresh status if running
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (isRefreshing) {
      interval = setInterval(async () => {
        try {
          const status = await getRefreshStatus()
          setRefreshStatus(status)
          setCurrentPhase(status.current_phase || '')
          setPhaseProgress(status.phase_progress || 0)
          setPhaseDescription(status.phase_description || '')
          if (status.status !== 'running') {
            setIsRefreshing(false)
            setCurrentPhase('')
            setPhaseProgress(0)
            setPhaseDescription('')
            // Reload dashboard data when refresh completes
            handleRefresh()
          }
        } catch (err) {
          console.error('Failed to poll refresh status:', err)
        }
      }, 2000) // Poll every 2 seconds
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isRefreshing, getRefreshStatus])

  // Get filters from URL parameters as primitives
  const territory_id = searchParams.get('territory_id') || null
  const date_from = searchParams.get('date_from') || null
  const date_to = searchParams.get('date_to') || null

  // Get other state from URL parameters (year is optional, only filter if explicitly provided)
  const cpvFilter = searchParams.get('cpv') || ''
  const yearParam = searchParams.get('year')
  const selectedYear = yearParam ? parseInt(yearParam) : undefined

  const allowedTabs = new Set(['dashboard', 'cpv'])

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'cpv', label: 'Kategorie CPV', icon: PieChart },
  ]

  // State for dashboard data (only needed for dashboard tab)
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    overview: null,
    cpvData: null,
    organizations: [],
    contractors: [],
  })
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [dashboardError, setDashboardError] = useState<string | null>(null)

  // Load dashboard data when filters change
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setDashboardLoading(true)
        setDashboardError(null)

        const [overviewData, cpvData, orgData, contractorData] = await Promise.all([
          getStatisticsOverview(territory_id, date_from, date_to),
          getCPVBreakdown(territory_id, date_from, date_to),
          getOrganizations(territory_id, cpvFilter || null, 5, date_from, date_to),
          getContractors(territory_id, cpvFilter || null, null, 5, date_from, date_to),
        ])

        setDashboardData({
          overview: overviewData,
          cpvData: cpvData,
          organizations: orgData.items || [],
          contractors: contractorData.items || [],
        })
      } catch (err) {
        console.error('Failed to load dashboard data:', err)
        setDashboardError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setDashboardLoading(false)
      }
    }

    loadDashboardData()
  }, [territory_id, date_from, date_to, cpvFilter])

  const handleTriggerRefresh = async () => {
    try {
      await refreshStatistics()
      setIsRefreshing(true)
      setRefreshStatus((prev: RefreshStatus | null) => ({ ...prev, status: 'running' }))
    } catch (err) {
      console.error('Failed to trigger refresh:', err)
      alert(
        'Nie udało się rozpocząć odświeżania: ' +
          (err instanceof Error ? err.message : 'Unknown error'),
      )
    }
  }

  // Filter update handlers
  const updateFilters = (newFilters: FilterUpdates) => {
    const params = new URLSearchParams(searchParams)

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) {
        params.set(key, String(value))
      } else {
        params.delete(key)
      }
    })

    setSearchParams(params)
  }

  const updateCPVFilter = (cpvCode: string) => {
    const params = new URLSearchParams(searchParams)
    if (cpvCode) {
      params.set('cpv', cpvCode)
    } else {
      params.delete('cpv')
    }
    setSearchParams(params)
  }

  // Navigation handlers
  const handleOrganizationClick = (orgName: string) => {
    navigate(`/organizations/${encodeURIComponent(orgName)}`)
  }

  const handleContractorClick = (contractorUuid: string) => {
    navigate(`/contractors/${contractorUuid}`)
  }

  const handleTabChange = (tabId: string) => {
    navigate(`/statistics/${tabId}`)
  }

  const handleRefresh = () => {
    // Force reload dashboard data
    setDashboardLoading(true)
    setDashboardError(null)
    // Trigger useEffect by updating dashboardData
    setDashboardData((prev: DashboardData) => ({ ...prev }))
  }

  // Get current active tab from pathname
  const getActiveTab = () => {
    const path = window.location.pathname
    if (path === '/statistics' || path === '/statistics/') return 'dashboard'
    const segments = path.split('/')
    return segments[2] || 'dashboard'
  }

  const activeTabRaw = getActiveTab()
  const activeTab = allowedTabs.has(activeTabRaw) ? activeTabRaw : 'dashboard'

  useEffect(() => {
    if (!allowedTabs.has(activeTabRaw)) {
      navigate('/statistics', { replace: true })
    }
  }, [activeTabRaw, navigate])

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-ink">Statystyki</h2>
          {refreshStatus && (
            <p className="text-sm text-ink-muted mt-1">
              Ostatnia aktualizacja:{' '}
              {refreshStatus.last_refresh
                ? new Date(refreshStatus.last_refresh).toLocaleString('pl-PL')
                : 'Nigdy'}
              {refreshStatus.status === 'running' && ' (Trwa aktualizacja...)'}
            </p>
          )}
        </div>
        <div className="flex space-x-4">
          <button
            onClick={handleTriggerRefresh}
            disabled={isRefreshing}
            className={`flex items-center px-4 py-2 rounded-md font-medium text-white ${
              isRefreshing ? 'bg-ink-faint cursor-not-allowed' : 'bg-success hover:bg-success'
            }`}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Aktualizowanie...' : 'Przelicz statystyki'}
          </button>
        </div>
      </div>

      {/* Progress Indicator */}
      {isRefreshing && currentPhase && (
        <div className="bg-brand-bg border border-brand rounded-md p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-brand">Trwa aktualizacja statystyk</h3>
            <span className="text-sm text-brand">{phaseProgress}%</span>
          </div>
          <p className="text-brand mb-2">{currentPhase}</p>
          {phaseDescription && <p className="text-brand text-sm mb-2">{phaseDescription}</p>}
          {refreshStatus?.records_total && (
            <p className="text-brand text-sm mb-2">
              Przetworzono: {refreshStatus.records_processed || 0} / {refreshStatus.records_total}{' '}
              ogłoszeń
            </p>
          )}
          <div className="w-full bg-brand-bg rounded-full h-2 mt-2">
            <div
              className="bg-brand h-2 rounded-full transition-all duration-300"
              style={{ width: `${phaseProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} tabs={tabs} />

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'dashboard' &&
          (dashboardLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
            </div>
          ) : dashboardError ? (
            <div className="bg-danger/5 border border-danger/20 rounded-md p-4">
              <p className="text-danger">Błąd podczas ładowania danych: {dashboardError}</p>
            </div>
          ) : (
            <DashboardTab
              overview={dashboardData.overview}
              cpvData={dashboardData.cpvData}
              organizations={dashboardData.organizations}
              contractors={dashboardData.contractors}
              onContractorClick={handleContractorClick}
            />
          ))}

        {activeTab === 'cpv' && (
          <CPVAnalysis
            year={selectedYear}
            onCPVClick={updateCPVFilter}
            onRefresh={handleTriggerRefresh}
          />
        )}
      </div>
    </div>
  )
}

export default StatisticsPanel
